import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { CampaignTrackingOption, CampaignSocialMediaType } from '@anytag/graphql';
import { MediaPreview, Anchor, Form } from '@anytag/shared-atoms';
import { AUTO_MANAGED_CAMPAIGN_TYPE_OPTIONS } from '@anytag/shared-constants';
import { useMarketplacePromotionMethodOptions, usePostPricesAvailability } from '../../hooks';
import { AnyCreatorIframePreview } from '../AnyCreatorIframePreview';
import {
  TitleField,
  BudgetField,
  CountryField,
  RevenueField,
  SalePICField,
  HashtagField,
  HashtagsNotice,
  AdvertiserField,
  MarginRateField,
  HubspotDealField,
  SocialMediaField,
  CostPerLikeField,
  CostPerPostField,
  CostPerViewField,
  CostPerShareField,
  CostPerOrderField,
  ApprovePostsField,
  FormLayoutTemplate,
  PreLaunchDateField,
  MaterialUploadField,
  PostRequirementField,
  ThumbnailUploadField,
  CostPerSaleTuneField,
  AgencyMarginRateField,
  CampaignCategoryField,
  AllowNewInfluencerField,
  InfluencerCategoryField,
  ServiceInformationField,
  ApproveInfluencersField,
  TrackingCreatorPostField,
  DraftPostSubmissionField,
  AdditionalRequirementField,
  InfluencerManagementPICField,
  CampaignPeriodWithLogicsField,
  InfluencerCommissionRateField
} from '../shared';
import { DefaultFormTemplateProps, MarketplaceFormKeys, MarketplaceFormValues } from '../types';
import { marketplaceSectionTitles } from '../utils';
import { AiRainbowSpinner, DescriptionGenerator, useDescriptionGeneratorState } from '../../DescriptionGenerator';

export const Template = ({ defaultValues, disabledFields, onSubmit }: DefaultFormTemplateProps) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext<MarketplaceFormValues>();
  const { isAdminStaff, isAdvertiser, isAdminStaffAgency } = useUserRoles();
  const { promotionMethodOptions } = useMarketplacePromotionMethodOptions();
  const descriptionActions = useDescriptionGeneratorState();

  const formValues = getValues();
  const hasTrackingPost = formValues.trackingOptions.includes(CampaignTrackingOption.POST);
  const allPostPrices = usePostPricesAvailability(formValues.socialMedia);
  const hashtagSectionIndex = hasTrackingPost ? 4 : 3;

  const handleTrackingPostChange = (active?: boolean) => {
    if (!active) {
      setValue(MarketplaceFormKeys.hashtags, []);
      setValue(MarketplaceFormKeys.requireDraftPost, false);
    }
    if (active && formValues.socialMedia === CampaignSocialMediaType.UNSELECT) {
      setValue(MarketplaceFormKeys.socialMedia, '');
    }
  };

  return (
    <FormLayoutTemplate defaultValues={defaultValues} onSubmit={onSubmit} hideSubmit={!isAdminStaff}>
      <Form.SectionTitle index={1} label={t(marketplaceSectionTitles.offerInformation)} />
      <p className="mb-3 text-base">{t('Select offer that you want to provide with special deal')}</p>

      <Form.Field className="flex items-center">
        <MediaPreview src={formValues.productThumbnail} className="h-30 w-30" />
        <Anchor
          lines={2}
          variant="blue"
          target="_blank"
          label={formValues.title}
          href={formValues.campaignUrl}
          className="ml-4 max-w-[464px] text-base uppercase"
        />
      </Form.Field>

      <Form.Fields itemsPerRow={2}>
        {isAdminStaffAgency ? <AdvertiserField disabled={disabledFields.advertiserId} required /> : null}
        {isAdminStaff ? (
          <>
            <HubspotDealField disabled={disabledFields.hubspotDealIds} />
            <SalePICField disabled={disabledFields.salesPicIds} required />
            <InfluencerManagementPICField disabled={disabledFields.influencerManagementPicIds} required />
          </>
        ) : null}
      </Form.Fields>

      <Form.Line className="mx-auto mt-3 mb-6" />
      <Form.SectionTitle index={2} label={t(marketplaceSectionTitles.campaignInformation)} />

      <TrackingCreatorPostField disabled={disabledFields.trackingOptions} onChange={handleTrackingPostChange} />

      <Form.Fields itemsPerRow={4}>
        <PreLaunchDateField disabled={disabledFields.preLaunchDate} className="max-w-1/2!" />
        <CampaignPeriodWithLogicsField disabled={disabledFields.period} required />
      </Form.Fields>
      <Form.Fields itemsPerRow={2}>
        <CountryField disabled={disabledFields.countryId} required />
        <CampaignCategoryField disabled={disabledFields.campaignCategoryId} required />
        <InfluencerCategoryField disabled={disabledFields.influencerCategoryIds} />
        <SocialMediaField
          required={hasTrackingPost}
          title={t('Selector.Social Media / Promotion method')}
          disabled={disabledFields.socialMedia}
          {...(!hasTrackingPost ? { options: promotionMethodOptions } : {})}
        />
      </Form.Fields>
      <Form.Field>
        <div className="border-base-border px-4">
          <ApproveInfluencersField disabled={disabledFields.isAutoInfluencerApproval} />
          <ApprovePostsField disabled={disabledFields.isAllowMultiplePosts} />
          <AllowNewInfluencerField disabled={disabledFields.allowNewInfluencer} />
        </div>
      </Form.Field>

      <DescriptionGenerator {...descriptionActions}>
        <Form.SectionTitle index={3} label={t(marketplaceSectionTitles.campaignDetails)} className="mt-2" />
      </DescriptionGenerator>

      <TitleField
        disabled={disabledFields.title || descriptionActions.loading}
        required
        labelLoader={descriptionActions.loading ? <AiRainbowSpinner className="ml-1" /> : undefined}
      />
      <ServiceInformationField
        disabled={disabledFields.serviceInformation || descriptionActions.loading}
        required
        labelLoader={descriptionActions.loading ? <AiRainbowSpinner className="ml-1" /> : undefined}
        getSunEditorInstance={descriptionActions.getSunEditorInstance}
      />
      <PostRequirementField
        disabled={disabledFields.requirement || descriptionActions.loading}
        required
        labelLoader={descriptionActions.loading ? <AiRainbowSpinner className="ml-1" /> : undefined}
        getSunEditorInstance={descriptionActions.getSunEditorInstance}
      />
      <ThumbnailUploadField disabled={disabledFields.thumbnails} required />
      <MaterialUploadField disabled={disabledFields.materials} />
      <AdditionalRequirementField disabled={disabledFields.additionalRequirement} />

      {hasTrackingPost ? (
        <>
          <DraftPostSubmissionField disabled={disabledFields.requireDraftPost} />
          <Form.Line className="mx-auto my-6" />
          <Form.SectionTitle
            index={hashtagSectionIndex}
            className="mb-6"
            label={t(marketplaceSectionTitles.trackingSettings)}
          />
          <HashtagsNotice />
          <HashtagField disabled={disabledFields.hashtags} />
          <div className="mb-6">
            <h3>
              {t('landingPageTitle_dynamic', {
                name: AUTO_MANAGED_CAMPAIGN_TYPE_OPTIONS.find((ct) => ct.value === formValues.autoManagedCampaignType)
                  ?.label
              })}
            </h3>
            <span className="text-sm text-tertiary">{t('landingPageDescription_1')}</span>
            <br />
            <span className="text-sm text-tertiary">{t('landingPageDescription_2')}</span>
          </div>
        </>
      ) : null}

      <Form.Line className="mx-auto mt-3 mb-6" />
      <Form.SectionTitle index={hashtagSectionIndex + 1} label={t(marketplaceSectionTitles.budgetSettings)} />

      <Form.Fields itemsPerRow={4}>
        {hasTrackingPost ? (
          <>
            {allPostPrices.costPerPost ? <CostPerPostField disabled={disabledFields.costPerPost} /> : null}
            {allPostPrices.costPerLike ? <CostPerLikeField disabled={disabledFields.costPerLike} /> : null}
            {allPostPrices.costPerView ? <CostPerViewField disabled={disabledFields.costPerView} /> : null}
            {allPostPrices.costPerShare ? <CostPerShareField disabled={disabledFields.costPerShare} /> : null}
          </>
        ) : null}
        {allPostPrices.costPerOrder ? <CostPerOrderField disabled={disabledFields.costPerOrder} /> : null}
        {allPostPrices.costPerSaleReferral ? <CostPerSaleTuneField disabled={disabledFields.costPerSaleTune} /> : null}
      </Form.Fields>

      <Form.Fields itemsPerRow={4}>
        <RevenueField disabled={disabledFields.maximumRevenuePerInfluencer} />
        {isAdminStaffAgency ? <AgencyMarginRateField disabled={disabledFields.agencyMarginRate} /> : null}
        {isAdvertiser ? <BudgetField disabled={disabledFields.budget} /> : null}
      </Form.Fields>

      <Form.Fields itemsPerRow={4}>
        {isAdminStaff ? <MarginRateField disabled={disabledFields.marginRate} required /> : null}
        {!isAdvertiser ? <InfluencerCommissionRateField /> : null}
        {!isAdvertiser ? <BudgetField disabled={disabledFields.budget} /> : null}
      </Form.Fields>

      <AnyCreatorIframePreview />
    </FormLayoutTemplate>
  );
};
